import { api } from './client'
import type { ApiEnvelope, PaginatedResponse, LibraryCategory, KnowledgeItem } from '@/types/api'

export interface KnowledgeQueryParams {
    fileName?: string
    fromDate?: string
    toDate?: string
}

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

function adaptPage<T>(raw: SpringPage<T> | PaginatedResponse<T>): PaginatedResponse<T> {
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements,
        page: raw.number + 1,
        limit: raw.size,
        totalPages: raw.totalPages,
    }
}

// --- CATEGORY APIs ---
export async function fetchCategories(
    page: number = 0,
    size: number = 10,
    kbName?: string,
    unitId?: string,
    signal?: AbortSignal
): Promise<PaginatedResponse<LibraryCategory>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<LibraryCategory>>>('/v1/robot/kb-management', {
            params: { page, size, ...(kbName ? { kbName } : {}), ...(unitId ? { unitId } : {}) },
            signal
        })
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh mục.';
        throw new Error(message);
    }
}

export async function createCategory(name: string): Promise<LibraryCategory> {
    try {
        const response = await api.post<ApiEnvelope<LibraryCategory>>('/v1/robot/kb-management', { name })
        return response.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tạo danh mục.';
        throw new Error(message);
    }
}

export async function updateCategory(kbId: string, name: string): Promise<LibraryCategory> {
    try {
        const response = await api.put<ApiEnvelope<LibraryCategory>>(`/v1/robot/kb-management/${kbId}`, { name })
        return response.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi cập nhật danh mục.';
        throw new Error(message);
    }
}

export async function deleteCategory(kbId: string): Promise<void> {
    try {
        await api.delete(`/v1/robot/kb-management/${kbId}`)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa danh mục.';
        throw new Error(message);
    }
}

// --- KNOWLEDGE ITEM APIs ---
export async function fetchKnowledgeItems(
    kbId: string,
    page: number = 0,
    size: number = 10,
    params: KnowledgeQueryParams = {},
    signal?: AbortSignal
): Promise<PaginatedResponse<KnowledgeItem>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<KnowledgeItem>>>(`/v1/robot/kb-management/get-file/${kbId}`, {
            params: { page, size, ...params },
            signal
        })
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách tài liệu.';
        throw new Error(message);
    }
}

export async function uploadKnowledgeItem(kbId: string, files: File[]): Promise<any> {
    try {
        const formData = new FormData()
        const allowedExtensions = ['.docx', '.pdf', '.txt', '.md']

        files.forEach((file) => {
            const actualFile = (file as any).originFileObj || file
            const fileName = actualFile.name.toLowerCase()

            const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext))

            if (!isValidExtension) {
                throw new Error(`File "${actualFile.name}" không hợp lệ. Chỉ hỗ trợ .docx, .pdf, .txt, .md`);
            }
            formData.append('files', actualFile)
        })

        const response = await api.post<ApiEnvelope<any>>(
            `/v1/robot/kb-management/${kbId}/upload/files`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' }
            }
        )
        return response.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tải lên tài liệu.';
        throw new Error(message);
    }
}

export async function deleteKnowledgeItem(kbId: string, docId: string): Promise<void> {
    try {
        await api.delete(`/v1/robot/kb-management/${kbId}/${docId}`)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa tài liệu.';
        throw new Error(message);
    }
}